import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { IMountingPlan } from "../models/mountingPlan";
import { IMountingPlanApiResponse } from "../models/api/mountingPlanApiResponse";
import { IMountingPlanFilter } from "../models/filters/mountingPlanFilter";
import MountingPlanService from "../services/MountingPlanService";
import { IMountingPlanForm } from "../models/forms/mountingPlanForm";
import { ServiceOperationResult } from "../models/api/serviceOperationResult";
import DataOperationError from "../models/api/dataOperationError";
import { IDataOperationResult } from "../models/api/dataOperationResult";
import { IBuilding } from "../models/building";
import { useBuildingStore } from "./building.store";
import signalRManager from "../helper/signalRManager";
import IPayloadStore from "../models/api/payloadStore";
import i18n from "@/i18n";

export const useMountingPlanStore = defineStore("mountingPlans", () => {
    // State
    const datas = ref<IMountingPlan[]>([]);
    const current = ref<IMountingPlan>({} as IMountingPlan);
    const itemForm = ref<IMountingPlanForm>({} as IMountingPlanForm);
    const isLoading = ref<boolean>(false);
    const buildingId = ref<number>(0);
    const displayPsLot = ref<boolean>(false);

    // Actions - Hydration
    /**
     * Hydrate MountingPlans from API response
     */
    async function hydrateMountingPlans(plans: IMountingPlanApiResponse[]): Promise<IMountingPlan[]> {
        if (plans.length === 0) return [];

        // 1. Get stores
        const buildingStore = useBuildingStore();

        // 2. Load in parallel if needed
        const loadPromises: Promise<unknown>[] = [];
        if (buildingStore.datas.length === 0) {
            loadPromises.push(buildingStore.load({ page: 1, pageSize: 0 }));
        }
        if (loadPromises.length > 0) {
            await Promise.all(loadPromises);
        }

        // 3. Create Maps for O(1) lookups
        const buildingMap = new Map(buildingStore.datas.map(b => [b.id, b]));

        // 4. Hydrate all entities in one pass
        const models: IMountingPlan[] = [];
        for (const plan of plans) {
            const model = { ...plan } as unknown as IMountingPlan;

            // Link references (O(1) via Map)
            model.building = buildingMap.get(plan.buildingId) ?? model.building;

            models.push(model);
        }

        return models;
    }

    // Getters
    const getMountingPlanDateAscending = computed(() => {
        // Use buildingId instead of building.id to avoid undefined access
        return datas.value.filter(item => item.buildingId === buildingId.value).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    });

    // Actions
    async function load(filter: IMountingPlanFilter) {
        isLoading.value = true;
        let result = await MountingPlanService.getAll<IMountingPlanFilter, IMountingPlanApiResponse>(filter);
        isLoading.value = false;
        datas.value = [];
        if (!result.error) {
            datas.value = await hydrateMountingPlans(result.data as IMountingPlanApiResponse[]);
        }
        return result;
    }

    async function get(id: number) {
        let result = await MountingPlanService.getById<IMountingPlanApiResponse>(id);
        if (!result.error) {
            const [modelData] = await hydrateMountingPlans([result.data as IMountingPlanApiResponse]);

            let index = datas.value.findIndex((a) => a.id == id);
            if (index >= 0)
                datas.value[index] = modelData;

            current.value = modelData;
            itemForm.value = toForm(modelData);
        }
        return result;
    }

    async function searchStoreById(id: number) {
        current.value = datas.value.find((objet) => objet.id == id)!;
        itemForm.value = toForm(current.value);
        return current.value;
    }

    async function save(maj: boolean = true, isPatch: boolean = false) {
        let result = {} as ServiceOperationResult<IMountingPlan | IDataOperationResult<IMountingPlan>>;
        if (itemForm.value.id > 0) {
            result = await MountingPlanService.update(itemForm.value);

            if (!result.error) {
                const [modelData] = await hydrateMountingPlans([result.data as IMountingPlanApiResponse]);
                current.value = modelData;

                if (maj) {
                    let index = datas.value.findIndex((a) => a.id == itemForm.value.id);
                    if (index >= 0) {
                        datas.value[index] = current.value;
                    } else {
                        const error = new DataOperationError();
                        error.REQUEST_ERROR = i18n.global.t("errors.itemNotFoundInStore");
                        return new ServiceOperationResult<IDataOperationResult<IMountingPlan>>(true, { errors: error });
                    }
                }
            }
        } else {
            result = await MountingPlanService.create(itemForm.value);
            if (!result.error) {
                const [modelData] = await hydrateMountingPlans([result.data as IMountingPlanApiResponse]);
                current.value = modelData;
            }
        }
        return result;
    }

    async function deleteAction() {
        let result = await MountingPlanService.delete<IMountingPlanApiResponse>(current.value.id, true);

        if (!result.error) {
            let index = datas.value.findIndex((a) => a.id == current.value.id);
            if (index >= 0) {
                datas.value.splice(index, 1);
            }

            current.value = newItem();
            itemForm.value = newForm();
        }
        return result;
    }

    async function batchDelete(ids: number[]) {
        isLoading.value = true;
        const result = await MountingPlanService.batchDelete(ids);
        if (!result.error) {
            datas.value = datas.value.filter(item => !ids.includes(item.id));
        }
        isLoading.value = false;
        return result;
    }

    function toForm(item: IMountingPlan): IMountingPlanForm {
        const form = {} as IMountingPlanForm;

        form.id = item.id;
        form.date = item.date;
        form.name = item.name;
        form.buildingId = item.building ? item.building.id : 0;

        itemForm.value = form;
        return form;
    }

    function newForm(lienId?: number): IMountingPlanForm {
        itemForm.value = {
            id: 0,
            date: new Date(),
            name: '',
            buildingId: lienId ?? 0,
        } as IMountingPlanForm;
        return itemForm.value;
    }

    function newItem(): IMountingPlan {
        current.value = {
            id: 0,
            date: new Date(),
            name: '',
            building: { id: 0 } as IBuilding,
        } as IMountingPlan;

        itemForm.value = toForm(current.value);
        return current.value;
    }

    return {
        // State
        datas,
        current,
        itemForm,
        isLoading,
        buildingId,
        displayPsLot,
        // Getters
        getMountingPlanDateAscending,
        // Actions
        hydrateMountingPlans,
        load,
        get,
        searchStoreById,
        save,
        delete: deleteAction,
        batchDelete,
        toForm,
        newForm,
        newItem
    };
});

signalRManager.connection.on("MajMountingPlan", async (payload: IPayloadStore) => {
    const store = useMountingPlanStore();
    switch (payload.action) {
        case "Create": {
            if (store.datas.findIndex((a) => a.id == payload.data?.id) == -1) {
                const [hydrated] = await store.hydrateMountingPlans([payload.data as IMountingPlanApiResponse]);
                store.datas.push(hydrated);
            }
            break;
        }
        case "Update": {
            const idx = store.datas.findIndex((a) => a.id == payload.data?.id);
            if (idx > -1) {
                const [hydrated] = await store.hydrateMountingPlans([payload.data as IMountingPlanApiResponse]);
                store.datas.splice(idx, 1, hydrated);
            }
            break;
        }
        case "Delete": {
            const idx = store.datas.findIndex((a) => a.id == payload.id);
            if (idx > -1) store.datas.splice(idx, 1);
            break;
        }
    }
});
